import React, { useEffect, useMemo, useState } from "react";
import { createStructuredSelector } from "reselect";
import { connect } from "react-redux";
import { isEmpty } from "lodash";

import { dhwRowHeadings, envelopeRowHeadings, hvacRowHeadings, otherRowHeadings } from "utils/upgrades";
import { actions as modelActions } from "features/Model/_ducks";
import { selectNzStudyReportPdfData } from "features/Model/_ducks/selectors";
import { selectUpgradePackages } from "features/Model/Upgrades/_ducks/selectors";

import Input from "components/Input";
import Button from "components/Button";
import InputSectionContainer from "components/PdfExport/InputSectionContainer";

import Delete from "assets/images/icons/JSX/Delete";

import classes from "../../styles.module.scss";

const { updateNzStudyPdfData } = modelActions;

const upgradeTableKeysToLabels = {
    Ceiling: "Ceiling with Attic Space",
    DHW: "Domestic Water Heater",
    Foundation: "Foundation Wall",
    Generation: "On-site Generation and Storage",
    Heating: "Space Heating",
    Cooling: "Cooling & Heat Pumps",
    Wall: "Above Grade Walls / Garage Wall",
    Ventilation: "Principle Ventilation",
    Window: "Windows & Sliding Glass Doors",
};

const UpgradesIncluded = ({
    upgradePackages,
    nzStudyReportPdfData = {},
    updateNzStudyPdfData,
    eFileUpgradePackages = {},
}) => {
    const { selectedUpgrade = "", nzUpgradesIncluded = [], nzPage2Content = {} } = nzStudyReportPdfData;

    const [currentSelectedUpgrade, setCurrentSelectedUpgrade] = useState(selectedUpgrade);
    const [upgradesLabels, setUpgradesLabels] = useState([]);

    // Determine if selected upgrade is from E-file
    const isEFileUpgrade = selectedUpgrade.startsWith("eFile_");
    const actualUpgradeKey = isEFileUpgrade ? selectedUpgrade.replace("eFile_", "") : selectedUpgrade;

    // Use E-file data if selected upgrade is from E-file, otherwise use current model data
    const postRetrofitUpgradePackages = isEFileUpgrade ? eFileUpgradePackages : upgradePackages;

    // By definition, this is the post retrofit upgrade packages
    const upgradePackage = postRetrofitUpgradePackages?.[actualUpgradeKey] || {};

    const maxColumnLength = 14;

    const allUpgradesLabels = useMemo(() => {
        if (isEFileUpgrade && actualUpgradeKey === "base") {
            const labelsFromEFileBaseCase = [];

            Object.keys(upgradeTableKeysToLabels).forEach((key) => {
                const { [`nzBefore${key}`]: beforeLabel, [`nzAfter${key}`]: afterLabel } = nzPage2Content || {};

                if (beforeLabel !== afterLabel) {
                    labelsFromEFileBaseCase.push(upgradeTableKeysToLabels[key]);
                }
            });

            return labelsFromEFileBaseCase.sort((a, b) => a.localeCompare(b));
        }

        const { upgrades = {} } = upgradePackage || {};

        const upgradeHeadings = [
            ...envelopeRowHeadings,
            ...hvacRowHeadings,
            ...dhwRowHeadings,
            ...otherRowHeadings,
        ].reduce(
            (acc, cur) => ({
                ...acc,
                [cur.id]: cur.content,
            }),
            {}
        );

        const labels = [];

        Object.keys(upgrades).forEach((key) => {
            const { label = {}, upgradeType = "" } = upgrades[key] || {};

            // Pushes upgrade table row header
            var labelToPush = upgradeHeadings[upgradeType];

            // If label object is missing default to using upgrade type
            if (!isEmpty(label)) {
                Object.keys(label).forEach((upgradeId) => {
                    labelToPush = upgradeHeadings[upgradeId];

                    if (!labels.includes(labelToPush) && label[upgradeId] !== "None") {
                        labels.push(labelToPush);
                    }
                });
            } else if (!labels.includes(labelToPush) && !!labelToPush) {
                labels.push(labelToPush);
            }
        });

        // Return labels sorted by shortest to longest to optimize column space
        if (labels.length >= maxColumnLength) {
            return labels.sort((a, b) => a.length - b.length);
        }

        return labels;
    }, [upgradePackage]);

    useEffect(() => {
        // Fill default upgrades if the selected upgrade is changed or the nzUpgradesIncluded array is empty
        if (isEmpty(nzUpgradesIncluded) || currentSelectedUpgrade !== selectedUpgrade) {
            restoreDefaultUpgrades();
            setCurrentSelectedUpgrade(selectedUpgrade);
        }
    }, [selectedUpgrade]);

    const restoreDefaultUpgrades = () => {
        setUpgradesLabels(allUpgradesLabels);
        updateNzStudyPdfData("nzUpgradesIncluded", allUpgradesLabels);
    };

    return (
        <InputSectionContainer title={"Upgrades Include"} contentStyle={{ height: "673.14px" }}
            warning={selectedUpgrade === "eFile_base" ? "chbaNzEfileBaseUpgradesIncludeWarning" : ""}
            info={selectedUpgrade === "eFile_base" ? "" : "Default upgrade labels were taken from the selected upgrade package."}
        >
            <div className={classes.buttonContainer}>
                <Button
                    onClick={() => updateNzStudyPdfData("nzUpgradesIncluded", [...nzUpgradesIncluded, ""])}
                    disabled={nzUpgradesIncluded.length >= 22}
                >
                    Add Upgrade
                </Button>
                <Button onClick={restoreDefaultUpgrades}>Restore Defaults</Button>
            </div>
            <div
                className={classes.upgradesIncludedGrid}
                style={{ gridTemplateColumns: nzUpgradesIncluded.length >= maxColumnLength ? "1fr 1fr" : "1fr" }}
            >
                {nzUpgradesIncluded.length > 0 ? (
                    nzUpgradesIncluded.map((label, index) => (
                        <div key={index} className={classes.upgrade}>
                            <Input
                                placeholder={upgradesLabels[index]}
                                hideLabel
                                input={{
                                    value: label,
                                    onChange: (value) => {
                                        const newUpgrades = [...nzUpgradesIncluded];

                                        newUpgrades[index] = value.slice(0, 50);

                                        updateNzStudyPdfData("nzUpgradesIncluded", newUpgrades);
                                    },
                                }}
                            />
                            <div
                                onClick={() => {
                                    // Remove the default upgrade label from the list
                                    if (index < upgradesLabels.length) {
                                        setUpgradesLabels(upgradesLabels.filter((_, i) => i !== index));
                                    }

                                    // Remove field from the nzUpgradesIncluded array
                                    updateNzStudyPdfData(
                                        "nzUpgradesIncluded",
                                        nzUpgradesIncluded.filter((_, i) => i !== index)
                                    );
                                }}
                                style={{ cursor: "pointer" }}
                            >
                                <Delete />
                            </div>
                        </div>
                    ))
                ) : (
                    <div> No Upgrades </div>
                )}
            </div>
        </InputSectionContainer>
    );
};

const mapStateToProps = createStructuredSelector({
    upgradePackages: selectUpgradePackages,
    nzStudyReportPdfData: selectNzStudyReportPdfData,
});

const mapDispatchToProps = (dispatch) => ({
    updateNzStudyPdfData: (fieldName, value) => dispatch(updateNzStudyPdfData(fieldName, value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UpgradesIncluded);
